import {
    Alignment, Api, CurrencyUtil, DisplayType, HorizontalAlignment, LogManager, ModelRow, Navigation, StringUtil
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    Button, ClickEvent, Component, ContextMenuItemCreator, ContextMenuLabelProps, DataDisplayEvent, DataSourceMode,
    Image, Label, Layout, Panel, SlideoutDecorator, Snackbar, Table, TableRow, TableRowDisplayEvent,
    TableRowExpansionEvent, TableSelectionEvent, Textbox
} from "@mcleod/components";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import {
    CommodityQuickInfo, FilterPanel, OrderTabView, SecondaryFilterProps
} from "@mcleod/dispatch";
import { CustomerQuickInfo } from "@mcleod/ar";
import { DataFusionLocationCrossReference } from "./DataFusionLocationCrossReference";
import { EdiProfileOrderQuickInfo } from "./EdiProfileOrderQuickInfo";
import { EdiSuggestedPricing } from "./EdiSuggestedPricing";
import { EdiUtility } from "./EdiUtility";
import { LoadTenderExpress } from "./LoadTenderExpress";
import { LoadTenderExpressExpanded } from "./LoadTenderExpressExpanded";
import { AutogenLayoutTenderGridLayout } from "./autogen/AutogenLayoutTenderGridLayout";
import { ModelEdiOrder } from "./models/ModelEdiOrder";
import { EdiRawDataSlideout } from "./EdiRawDataSlideout";
import { PurposeSwapDialog } from "./PurposeSwapDialog";
import { PurposeSwap } from "./PurposeSwap";
import { LinkTenderDialog } from "./LinkTenderDialog";
import { ModelEdiorderProfile } from "./models/ModelEdiorderProfile";
import { getTimeDiff } from "@mcleod/powerbroker/src/BrokeragePlanningUtil";

const log = LogManager.getLogger("lme/datafusion/TenderGridLayout");

const REPLY_NOT_SENT = "RU";
const REPLY_SENT = "RS";
const REPLY_ERROR = "RE";
const REPLY_AUTO_SEND_ERROR = "RA";
const REPLY_NOT_REQUIRED = "RN";
const REPLY_AUTO_FAILED = "RA";
const TENDER_SUCCESSFUL = "TS";
const TENDER_ERROR = "TE";
const TENDER_NEEDS_ATTENTION = "TA";
const TENDER_NOT_PROCESSED = "TN";
const furtherActionNeeded = (row: ModelRow) => {
    const statusLabel = row.get("status_label");
    return (REPLY_NOT_REQUIRED + TENDER_SUCCESSFUL) == statusLabel ||
        (REPLY_NOT_REQUIRED + TENDER_NOT_PROCESSED) == statusLabel ||
        (REPLY_NOT_SENT + TENDER_SUCCESSFUL) == statusLabel ||
        (REPLY_NOT_SENT + TENDER_NOT_PROCESSED) == statusLabel;
}

interface WarningClockState {
    visible: boolean;
    imageName?: ImageName;
    imageColor?: string;
    tooltip?: string;
}

const FILTER_EXPIRING: SecondaryFilterProps = {
    id: "expiring",
    caption: "Expiring",
    filter: (row: ModelRow) => row.get("must_respond_time_left") <= 60 && row.get("must_respond_time_left") > 0
}

const FILTER_EXPIRED: SecondaryFilterProps = {
    id: "expired",
    caption: "Expired",
    filter: (row: ModelRow) => row.get("must_respond_time_left") <= 0
}

const FILTER_FURTHER_ACTION: SecondaryFilterProps = {
    id: "furtherAction",
    caption: "Further Action Needed",
    filter: (row: ModelRow) => {
        return !furtherActionNeeded(row);
    }
}

const FILTER_MISSING_LOCATION_XREF: SecondaryFilterProps = {
    id: "missingXrefCount",
    caption: "Missing Location Xref",
    filter: (row: ModelRow) => "Y" == row.get("missing_xref_label")
}

const FILTER_SHIPPER_MISSING_LOCATION_XREF: SecondaryFilterProps = {
    id: "shipperMissingXrefCount",
    caption: "Shipper Missing Xref",
    filter: (row: ModelRow) => "Y" === row.get("missing_xref_label") && "" === row.get("origin_location_id")
}

export class TenderGridLayout extends AutogenLayoutTenderGridLayout {
    selectedRow: TableRow;
    selectedRows: TableRow[];
    public recordCount: number;
    public isBusy: boolean;
    private _loadTenderExpress: LoadTenderExpress;
    private _shipperLocReq: string = "N";
    private _filterPanel: FilterPanel;
    private _clockTimerId: number;
    public secondaryFilters = [
        FILTER_EXPIRING,
        FILTER_EXPIRED,
        FILTER_FURTHER_ACTION,
        FILTER_MISSING_LOCATION_XREF,
        FILTER_SHIPPER_MISSING_LOCATION_XREF
    ];

    override onLoad() {
        this.loadControlSettings();
        this.tableLtx.doOnResolveRowEdit = async (updatedRow: ModelRow) => {
            this._loadTenderExpress.buttonRefresh.busy = true;
            const selectedRowId: string = this.tableLtx?.selectedRow?.data?.get("id");
            await this._loadTenderExpress.refreshScreen();
            const foundIndex: number = this.tableLtx.rows.findIndex(record =>
                record.data.get("tender_id") === selectedRowId
            );
            this.tableLtx.selectedIndex = foundIndex < 0 ? 0 : foundIndex;
            this._loadTenderExpress.buttonRefresh.busy = false;
            return null;
        };
        this._filterPanel = new FilterPanel(this.secondaryFilters, this.tableLtx, this.panelSecondaryFilters);
        this.startClockTimer();

        this.tableLtx.addSelectionListener(event => {
            if(this.tableLtx.selectedIndex < 0){
                this.buttonPurposeSwap.tooltip = "Purpose Swap";
                this.buttonPurposeSwap.enabled = false;
                this.buttonPurposeSwap.imageColor = "strokeSecondary";
                this.buttonLinkTender.tooltip = "Link Change Tender";
                this.buttonLinkTender.enabled = false;
                this.buttonLinkTender.imageColor = "strokeSecondary";
                this.buttonRawData.enabled = false;
                this.buttonRawData.imageColor = "strokeSecondary";
                this.buttonSuggestedPricing.enabled = false;
                this.buttonSuggestedPricing.imageColor = "strokeSecondary";
            }
        });
    }

    onRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;

        // Drill down option requires modelRow to have a field called "id"
        const id = modelRow?.get("tender_id");
        modelRow?.set("id", id);

        const shipmentTenders: Label = tableRow.findComponentById("labelDupTenderID") as Label;
        const lockedLabel = tableRow.findComponentById("labelLocked") as Label;
        const labelTotalCharges = tableRow.findComponentById("labelTotalCharges") as Label;
        const labelOriginName = tableRow.findComponentById("labelOriginName") as Label;
        const labelDestinationName = tableRow.findComponentById("labelDestName") as Label;
        const textboxTenderId = tableRow.findComponentById("textboxTenderId") as Textbox;
        textboxTenderId.addUnderlineWhenMouseOver();

        if ("Y" == tableRow.data.get("record_in_use")) {
            lockedLabel.visible = true;
            lockedLabel.tooltipPosition = Alignment.RIGHT;
            lockedLabel.text = '';
        }
        else {
            lockedLabel.visible = false;
        }

        if (tableRow.data.get("shipment_id_count") <= 1) {
            shipmentTenders.visible = false;
        } else {
            shipmentTenders.visible = true;
        }

        this.setTooltipContent(labelTotalCharges, modelRow);
        this.setupPartnerNameLabelTooltipContent(tableRow);
        this.setupCustomerTooltipContent(tableRow);

        const shipperLocationID = modelRow.get("origin_location_id");

        if(shipperLocationID != undefined && labelOriginName != undefined && !StringUtil.isEmptyString(shipperLocationID)) {
            labelOriginName.quickInfoOverrideKeyValue = shipperLocationID;
        } else {
            labelOriginName.quickInfoLayout = null;
        }

        const consigneeLocationID = modelRow.get("destination_location_id");

        if(consigneeLocationID != undefined && labelDestinationName != undefined && !StringUtil.isEmptyString(consigneeLocationID)) {
            labelDestinationName.quickInfoOverrideKeyValue = consigneeLocationID;
        } else {
            labelDestinationName.quickInfoLayout = null;
        }

        const labelOrderId = tableRow.findComponentById("labelOrderId") as Label;
        const orderId = modelRow.data["order_id"];
        if (!StringUtil.isEmptyString(orderId)) {
            labelOrderId.text = "[" + orderId + "]";
            labelOrderId.addUnderlineWhenMouseOver();
            labelOrderId.visible = true;
        } else {
            labelOrderId.visible = false;
        }

        const buttonCommitment = tableRow.findComponentById("buttonCommitment") as Button;
        const commitmentId = modelRow.get("commitment_id");
        const origDestRateId = modelRow.get("orig_dest_rate_id");
        if (!commitmentId || !origDestRateId) buttonCommitment.visible = false;

        const stopsLabel = tableRow.findComponentById("labelStopPicks") as Label;
        stopsLabel.applyLinkStyle();
        stopsLabel.addClickListener(() => {
            tableRow.expanded = !tableRow.expanded;
        });

        const labelOrigSchedLate = modelRow.get("origin_sched_late");
        if (labelOrigSchedLate === "" || labelOrigSchedLate === null || labelOrigSchedLate === undefined) {
            const labelDashOrig = tableRow.findComponentById("labelDashOrig") as Label;
            labelDashOrig.visible = false;
        }
        const labelDestSchedLate = modelRow.get("destination_sched_late");
        if (labelDestSchedLate === "" || labelDestSchedLate === null || labelDestSchedLate == undefined) {
            const labelDashDest = tableRow.findComponentById("labelDashDest") as Label;
            labelDashDest.visible = false;
        }
        this.setupTextboxes(tableRow);

        const imageHazmat = tableRow.findComponentById("imageHazmat") as Image;
        if (!modelRow.isNull("hazmat_code") || modelRow.get("hazmat") || modelRow.get("commodity_is_hazmat") ) imageHazmat.visible = true;
        this.setCommodityDescTooltipContent(tableRow, modelRow);
        tableRow.contextMenuItems = () => this.getContextMenuItems(tableRow, id);
    }

    private setCommodityDescTooltipContent(tableRow: TableRow, data: ModelRow) {
        const textboxCommodityDesc = tableRow.findComponentById("textboxCommodityDesc") as Textbox;
        textboxCommodityDesc.tooltipDynamicContent = async () => {
            const commodityId = data.get("commodity_id");
            if (textboxCommodityDesc.text !== "--" && !StringUtil.isEmptyString(commodityId)) {
                const layout = await Layout.getLoadedLayout("lme/dispatch/CommodityQuickInfo") as CommodityQuickInfo;
                await layout.mainDataSource?.search({ id: commodityId });
                return layout;
            }
        };
    }

    public set LoadTenderExpress(ltx: LoadTenderExpress) {
        this._loadTenderExpress = ltx;
    }

    onClickTenderCount(event: ClickEvent) {
        if (event.target instanceof Label) {
            const shipment_id = event.target.boundRow.get("shipment_id");
            const allLayout = this._loadTenderExpress.layoutAll;
            const allTab = this._loadTenderExpress.tabAll;

            allTab.select();
            allLayout.tableLtx.setFilter(`shipment_id=${shipment_id}`);
        }
    }

    setupPartnerNameLabelTooltipContent(row: TableRow) {
        const partnerNameLabel = row.findComponentById("labelPartnerName") as Label;
        partnerNameLabel.tooltipDynamicContent = async () => {
            const boundRow = partnerNameLabel.getRelevantModelRow();
            const partnerID = boundRow.get("partner_id");
            const version = boundRow.get("version");
            const direction = boundRow.get("direction");
            const batch = boundRow.get("batch_number");
            const createDate = boundRow.get("create_date");
            if (partnerID != null && version != null && direction != null) {
                const layout = await Layout.getLoadedLayout("lme/datafusion/EdiProfileOrderQuickInfo") as EdiProfileOrderQuickInfo;
                await layout.sourceEdiorderProfile?.search({ partner_id: partnerID, version: version, direction: direction, batch: batch, trans_type: 'T', create_date: createDate });
                return layout;
            }
            return null;
        }
    }

    setupCustomerTooltipContent(row: TableRow) {
        const partnerNameLabel = row.findComponentById("labelCustomer") as Label;
        partnerNameLabel.tooltipDynamicContent = async () => {
            const customerID = partnerNameLabel.boundRow?.get("customer_id");
            if (!StringUtil.isEmptyString(customerID)) {
                const layout = await Layout.getLoadedLayout("lme/ar/CustomerQuickInfo") as CustomerQuickInfo;
                await layout.sourceCustomer?.search({ id: customerID });
                return layout;
            }
            return null;
        }
    }

    displayMissingXref(event: DataDisplayEvent) {
        const imageXref = <Image>event.target;
        const currentRow = <ModelRow>event.rowData;
        imageXref.visible = false;
        if ("Y" == currentRow.get("missing_xref_label")) {
            imageXref.tooltip = "One or more stops is missing a location cross reference.";
            imageXref.tooltipPosition = Alignment.RIGHT;
            imageXref.visible = true;

            if(currentRow.get("origin_location_id") === "" && this._shipperLocReq === "Y"){
                imageXref.color = "error";
            }
        }
    }
    displayStatusIcon(event: DataDisplayEvent) {
        const statusLabel = <Image>event.target;
        const currentRow = <ModelRow>event.rowData;


        statusLabel.tooltipPosition = Alignment.RIGHT;
        statusLabel.visible = true;
        if (furtherActionNeeded(currentRow)) { //reply successful (or not required), transaction successful
            statusLabel.visible = false;
        }
        else if ((REPLY_ERROR + TENDER_NOT_PROCESSED) == currentRow.get("status_label")) {
            statusLabel.name = ImageName.dfReplyErrorNeedsAtt;
            statusLabel.marginBottom = 1;
            statusLabel.tooltip = "Reply has errors, tender needs attention";
        }
        else if ((REPLY_ERROR + TENDER_SUCCESSFUL) == currentRow.get("status_label")) {
            statusLabel.name = ImageName.dfReplyError;
            statusLabel.marginBottom = 1;
            statusLabel.tooltip = "Reply has errors";
        }
        else if ((REPLY_SENT + TENDER_ERROR) == currentRow.get("status_label")) { //reply successful, transaction error
            statusLabel.name = ImageName.dfReplyGoodTransError;
            statusLabel.tooltip = "Tender has errors, tender replied to successfully";
        }
        else if ((REPLY_SENT + TENDER_NEEDS_ATTENTION) == currentRow.get("status_label") || (REPLY_SENT + TENDER_NOT_PROCESSED) == currentRow.get("status_label")) { //reply successful, transaction needs attention (failed auto-processing)
            statusLabel.name = ImageName.dfReplyGoodNeedsAtt;
            statusLabel.tooltip = "Reply successful, order needs attention";
        }
        else if ((REPLY_ERROR + TENDER_ERROR) == currentRow.get("status_label")) { //reply error, transaction error
            statusLabel.name = ImageName.dfReplyErrorTransError;
            statusLabel.tooltip = "Tender and reply have errors";
        }
        else if ((REPLY_ERROR + TENDER_NEEDS_ATTENTION) == currentRow.get("status_label")) { //reply error, transaction needs attention (failed auto-processing)
            statusLabel.name = ImageName.dfReplyErrorNeedsAtt;

            if (currentRow.get("is_original")) {
                statusLabel.tooltip = "Reply has errors, order has not been created";
            }
            else if (currentRow.get("is_change")) {
                statusLabel.tooltip = "Reply has errors, order has not been compared";
            }
            else if (currentRow.get("is_cancel")) {
                statusLabel.tooltip = "Reply has errors, order has not been voided";
            }
            else {
                statusLabel.tooltip = "Unknown transaction type failed, reply has errors";
            }
        }
        else if ((REPLY_NOT_REQUIRED + TENDER_ERROR) == currentRow.get("status_label") || (REPLY_NOT_SENT + TENDER_ERROR) == currentRow.get("status_label")) { //reply not required, transaction error
            statusLabel.name = ImageName.dfNoReplyTransError;
            statusLabel.tooltip = "Tender has errors";
        }
        else if ((REPLY_NOT_REQUIRED + TENDER_NEEDS_ATTENTION) == currentRow.get("status_label") || (REPLY_NOT_SENT + TENDER_NEEDS_ATTENTION) == currentRow.get("status_label")) { //reply not required, transaction needs attention (failed auto-processing)
            statusLabel.name = ImageName.dfNoReplyNeedsAtt;  //this needs a new image when available

            if (currentRow.get("is_original")) {
                statusLabel.tooltip = "Order has not been created";
            }
            else if (currentRow.get("is_change")) {
                statusLabel.tooltip = "Order has not been compared";
            }
            else if (currentRow.get("is_cancel")) {
                statusLabel.tooltip = "Order has not been voided";
            }
            else {
                statusLabel.tooltip = "Transaction failed";
            }
        }
        else if ((REPLY_AUTO_FAILED + TENDER_NEEDS_ATTENTION) == currentRow.get("status_label")) { //auto reply failed, transaction needs attention (failed auto-processing)
            statusLabel.name = ImageName.dfReplyErrorNeedsAtt;

            if (currentRow.get("is_original")) {
                statusLabel.tooltip = "Auto reply failed, order has not been created";
            }
            else if (currentRow.get("is_change")) {
                statusLabel.tooltip = "Auto reply failed, order has not been compared";
            }
            else if (currentRow.get("is_cancel")) {
                statusLabel.tooltip = "Auto reply failed, order has not been voided";
            }
            else {
                statusLabel.tooltip = "Unknown transaction type failed, auto reply failed";
            }
        }
        else if ((REPLY_AUTO_FAILED + TENDER_ERROR) == currentRow.get("status_label")) { //reply error, transaction error
            statusLabel.name = ImageName.dfReplyErrorTransError;
            statusLabel.tooltip = "Auto reply failed and tender has errors";
        }
        else if ((REPLY_AUTO_FAILED + TENDER_NOT_PROCESSED) == currentRow.get("status_label")) {
            statusLabel.name = ImageName.dfReplyErrorNeedsAtt;
            statusLabel.marginBottom = 1;
            statusLabel.tooltip = "Auto reply failed, tender needs attention";
        }
        else if ((REPLY_AUTO_FAILED + TENDER_SUCCESSFUL) == currentRow.get("status_label")) {
            statusLabel.name = ImageName.dfReplyError;
            statusLabel.marginBottom = 1;
            statusLabel.tooltip = "Auto reply failed";
        }
        else if ((REPLY_AUTO_SEND_ERROR + TENDER_NOT_PROCESSED) == currentRow.get("status_label")) {
            statusLabel.name = ImageName.dfReplyErrorNeedsAtt;
            statusLabel.marginBottom = 1;
            statusLabel.tooltip = "Auto-send reply did not transmit, tender needs attention";
        }
        else if ((REPLY_AUTO_SEND_ERROR + TENDER_SUCCESSFUL) == currentRow.get("status_label")) {
            statusLabel.name = ImageName.dfReplyError;
            statusLabel.marginBottom = 1;
            statusLabel.tooltip = "Auto-send reply did not transmit";
        }
        else if ((REPLY_AUTO_SEND_ERROR + TENDER_ERROR) == currentRow.get("status_label")) { //reply error, transaction error
            statusLabel.name = ImageName.dfReplyErrorTransError;
            statusLabel.tooltip = "Auto-send reply did not transmit and tender has errors";
        }
        else if ((REPLY_AUTO_SEND_ERROR + TENDER_NEEDS_ATTENTION) == currentRow.get("status_label")) { //reply error, transaction needs attention (failed auto-processing)
            statusLabel.name = ImageName.dfReplyErrorNeedsAtt;

            if (currentRow.get("is_original")) {
                statusLabel.tooltip = "Auto-send reply did not transmit, order has not been created";
            }
            else if (currentRow.get("is_change")) {
                statusLabel.tooltip = "Auto-send reply did not transmit, order has not been compared";
            }
            else if (currentRow.get("is_cancel")) {
                statusLabel.tooltip = "Auto-send reply did not transmit, order has not been voided";
            }
            else {
                statusLabel.tooltip = "Unknown transaction type failed, reply has errors";
            }
        }
        else {
            statusLabel.visible = false;
        }
    }

    displayWarningClock(event: DataDisplayEvent) {
        const clockLabel = event.target as Label;
        const currentRow = event.rowData as ModelRow;
        const respondByDate = this.parseMustRespondBy(currentRow.get("must_respond_by") ?? null);
        const state = this.getWarningClockState(respondByDate);
        clockLabel.visible = state.visible;
        if (state.visible) {
            clockLabel.imageName = state.imageName;
            clockLabel.imageColor = state.imageColor;
        }
        clockLabel.tooltipDynamicContent = () => {
            const currentState = this.getWarningClockState(this.parseMustRespondBy(currentRow.get("must_respond_by") ?? null));
            if (!currentState.visible || !currentState.tooltip) return null;
            clockLabel.imageName = currentState.imageName;
            clockLabel.imageColor = currentState.imageColor;
            return new Label({ text: currentState.tooltip });
        };
    }

    private startClockTimer() {
        this._clockTimerId = window.setTimeout(() => {
            this.updateAllWarningClocks();
            this.startClockTimer();
        }, (60 - new Date().getSeconds()) * 1000);
    }

    public clearClockTimer() {
        if (this._clockTimerId) {
            clearTimeout(this._clockTimerId);
            this._clockTimerId = null;
        }
    }

    private updateAllWarningClocks() {
        for (const row of this.tableLtx.allRows) {
            if (!row.populatedDOM)
                continue;
            this.updateWarningClock(row);
        }
    }

    private updateWarningClock(tableRow: TableRow) {
        const clockLabel = tableRow.findComponentById("labelWarningClock") as Label;
        if (clockLabel == null)
            return;
        const respondByDate = this.parseMustRespondBy(tableRow.data?.get("must_respond_by") ?? null);
        const state = this.getWarningClockState(respondByDate);
        clockLabel.visible = state.visible;
        if (!state.visible)
            return;
        clockLabel.imageName = state.imageName;
        clockLabel.imageColor = state.imageColor;
    }

    displayRespondByTimezone(event: DataDisplayEvent) {
        const timezoneLabel = <Label>event.target;
        const currentRow = <ModelRow>event.rowData;
        const respondBy = currentRow.get("must_respond_by");
        timezoneLabel.visible = (respondBy != null && respondBy != "");
    }

    tableLtxOnRowExpand(event: TableRowExpansionEvent) {
        if ((event.target as TableRow).data != null) {
            const row = (event.target as TableRow).data;
            const tenderID = row.get("tender_id");
            const purpose = row.get("purpose_desc");
            const orderID = row.get("order_id");
            const templateType = row.get("template_format");
            const replyCreated = row.get("reply_created") ? "Y" : "N";
            const expandComp = (event.expandComponentParent as Panel).components[0] as LoadTenderExpressExpanded;
            expandComp.tenderID = tenderID;
            expandComp.purposeType = purpose;
            expandComp.orderID = orderID;
            expandComp.templateType = templateType;
            expandComp.replyCreated = replyCreated;
            expandComp.loadTenderExpress = this._loadTenderExpress;
            expandComp.initialize();
        }
    }

    tableLtxOnSelect(event: TableSelectionEvent) {
        this.selectedRows = (event.target as Table).selectedRows;
        this.selectedRow = (event.target as Table).selectedRow;
        if (this._loadTenderExpress) {
            this._loadTenderExpress.selectedRows = this.selectedRows;
            this._loadTenderExpress.selectedRow = this.selectedRow;
        }

        if (this.selectedRow != null) {
            const purposeType = this.selectedRow.data.get("purpose_desc");

            if ((this.selectedRow.data.get("initial_purpose") === "O" && this.selectedRow.data.get("requires_reply_original") === "Y") && this.selectedRow.data.get("reply_created") !== "Y") {
                this._loadTenderExpress.enableReplyOptions();
            }
            else if ((this.selectedRow.data.get("initial_purpose") === "U" && this.selectedRow.data.get("requires_reply_change") === "Y") && this.selectedRow.data.get("reply_created") !== "Y") {
                this._loadTenderExpress.enableReplyOptions();
            }
            else if ((this.selectedRow.data.get("initial_purpose") === "C" && this.selectedRow.data.get("requires_reply_cancel") === "Y") && this.selectedRow.data.get("reply_created") !== "Y") {
                this._loadTenderExpress.enableReplyOptions();
            }
            else {
                this._loadTenderExpress.enableProcessOption();
            }

            //Action buttons Raw Data, Purpose Swap, and Link Change Tender
            this.buttonRawData.enabled = true;
            this.buttonRawData.imageColor = "primary";
            this.buttonSuggestedPricing.enabled = true;
            this.buttonSuggestedPricing.imageColor = "primary";
            if (purposeType != "Cancel" && (purposeType === "Original" || (purposeType != null && purposeType.indexOf("Change") >= 0))) {
                this.buttonPurposeSwap.enabled = true;
                this.buttonPurposeSwap.imageColor = "primary";
                this.buttonLinkTender.tooltip = "Link a change tender to an order";
                this.buttonLinkTender.enabled = true;
                this.buttonLinkTender.imageColor = "primary";

                if (purposeType === "Original") {
                    this.buttonPurposeSwap.tooltip = "Change purpose to 'Change Tender'";
                    this.buttonLinkTender.enabled = false;
                    this.buttonLinkTender.tooltip = "Link Change Tender";
                    this.buttonLinkTender.imageColor = "strokeSecondary";
                }

                if (purposeType != null && purposeType.indexOf("Change") >= 0) {
                    this.buttonPurposeSwap.tooltip = "Change purpose to 'Original Tender'";
                }
            }
            if(purposeType === "Cancel"){
                this.buttonPurposeSwap.enabled = false;
                this.buttonPurposeSwap.tooltip = "Purpose Swap";
                this.buttonPurposeSwap.imageColor = "strokeSecondary";
                this.buttonLinkTender.enabled = false;
                this.buttonLinkTender.tooltip = "Link Change Tender";
                this.buttonLinkTender.imageColor = "strokeSecondary";
            }

            if (this.selectedRow.data.get("reply_action") === "D") {
                this.buttonPurposeSwap.enabled = false;
                this.buttonPurposeSwap.imageColor = "strokeSecondary";
            }

        }
    }

    createXref(event: ClickEvent) {
        const xref = event.target["row"].data.get("missing_xref_label");
        const row = event.target["row"] as TableRow;
        const xRefImage = row.findComponentById("imageMissingCrossReference");
        if ("N" == xref) {
            return;
        }
        const button = event.target as Button;
        button.busy = true;
        log.debug("In createXref");
        const tenderID = event.target["row"].data.get("tender_id");
        const shipmentId = event.target["row"].data.get("shipment_id");
        const layout = Layout.getLayout("lme/datafusion/DataFusionLocationCrossReference", { paddingBottom: 50 }) as DataFusionLocationCrossReference;
        layout.tenderId = tenderID;
        layout.ediOrderRow = event.target["row"];
        layout.shipperLocReq = this._shipperLocReq;
        layout.fillHeight = true;
        const saveButton = new Button({
            caption: "Save",
            align: HorizontalAlignment.RIGHT,
            color: "McLeodWhite",
            id: "buttonSave",
            onClick: async (event) => {
                saveButton.busy = true;
                await layout.saveOnClick(event)
                saveButton.busy = false;
            },
            rowBreak: false
        })
        //need to look at main datasource, get stops and pass to layout
        new SlideoutDecorator({
            layout: layout,
            title: "Location Cross Reference - Shipment ID " + shipmentId,
            fillVerticalSpace: true,
            width: window.innerWidth * 0.35,
            addlComponents: saveButton,
            doAfterSlideOut: (decorator: SlideoutDecorator) => {
                button.busy = false;
                if(row.data.get("origin_location_id") !== ""){
                    xRefImage.color = "warning";
                }
            }
        });
    }

    loadControlSettings() {
        const input = { _field_list: { extraFields: "lme/general/dispatch-control-enforce-shipper-id" } };
        Api.search("lme/general/dispatch-control", input).then(response => {
            const data = response?.data?.[0];
            this._shipperLocReq = data?.enforce_shipper_id;
        });
    }

    async unlockTender(event: ClickEvent) {
        const row = event.target as Label;
        const ediOrderRow = await new ModelEdiOrder().searchSingle({ id: row.boundRow.get("tender_id") });
        CommonDialogs.showYesNo("Are you sure you want to unlock this tender?", "Confirm Unlock").then(async clickedYes => {
            if (clickedYes) {
                ediOrderRow.set("record_in_use", "N");
                await ediOrderRow.post();
                this._loadTenderExpress.refreshScreen();
            }
        })
    }

    setTooltipContent(comp: Component, modelRow: ModelRow) {
        if (comp.id == null) return;
        switch (comp.id) {
            case "labelTotalCharges":
                return CommonTooltips.setTooltipContentTextboxes(comp, () => [
                    { caption: "Type", displayType: DisplayType.STRING, text: modelRow.get("rate_type"), rowBreak: false, fillRow: true },
                    { caption: "Rate", displayType: DisplayType.FLOAT, text: modelRow.get("rate"), marginLeft: 75 },
                    { caption: "Units", displayType: DisplayType.STRING, text: modelRow.get("rate_units"), rowBreak: false, fillRow: true },
                    { caption: "Freight Charge", displayType: DisplayType.FLOAT, text: CurrencyUtil.formatCurrency(modelRow.get("freight_charge")), marginLeft: 20 },
                    { caption: "Rate ID", displayType: DisplayType.STRING, text: modelRow.get("rate_id"), rowBreak: false, fillRow: true },
                    { caption: "Other Charges", displayType: DisplayType.FLOAT, text: CurrencyUtil.formatCurrency(modelRow.get("other_charge_total")), marginLeft: 10 }
                ]);
        }
    }

    labelOrderIdOnClick(event: ClickEvent) {
        const orderId = (event.target as Label).text.replace("[", "").replace("]", "");
        OrderTabView.navigateTo({ key: orderId, newTab: true });

    }

    buttonSuggestedPricingOnClick(event: ClickEvent) {
        if (this.selectedRows == null || this.selectedRows.length == 0 || this.tableLtx.selectedRow == null) {
            Snackbar.showWarningSnackbar("Please select a tender to view its suggested pricing");
            return;
        }
        else if (this.selectedRows.length > 1) {
            Snackbar.showWarningSnackbar("You must select exactly one tender to view its suggested pricing")
            return;
        }
        const button = event.target as Button;
        button.busy = true;
        const tableRow: TableRow = this.tableLtx?.selectedRow;
        const row: ModelRow = tableRow.data;
        this.openSuggestedPricingSlideup(button, row, "tabLaneHistory");
    }

    buttonCommitmentOnClick(event: ClickEvent) {
        const button = event.target as Button;
        button.busy = true;
        const tableRow = TableRow.getContainingTableRow(button);
        tableRow.table.selectedRow = tableRow;
        const row: ModelRow = tableRow.data;
        this.openSuggestedPricingSlideup(button, row, "tabCommitmentAutoRating");
    }

    openSuggestedPricingSlideup(button: Button, row: ModelRow, tabId: string) {
        const tenderID = row.get("tender_id");
        const ediSuggestedPricingLayout: EdiSuggestedPricing = Layout.getLayout("lme/datafusion/EdiSuggestedPricing") as EdiSuggestedPricing;

        const excludeButton = ediSuggestedPricingLayout.excludeButton;

        const setButtonsActive = (declineButtonEnabled: boolean) => {
            if (acceptButton != null) acceptButton.enabled = true;
            if (acceptButton != null) acceptButton.visible = true;
            if (declineButton != null) declineButton.enabled = declineButtonEnabled;
            if (declineButton != null) declineButton.visible = true;
            if (excludeButton != null) excludeButton.enabled = true;
            if (excludeButton != null) excludeButton.visible = true;
        }
        ediSuggestedPricingLayout.excludeButtonCallback = () => {
            ediSuggestedPricingLayout.slideOut();
            setTimeout(() => this._loadTenderExpress.excludeTender(), 250);
        };

        const declineButton = ediSuggestedPricingLayout.declineButton;
        ediSuggestedPricingLayout.declineButtonCallback = () => {
            ediSuggestedPricingLayout.slideOut();
            setTimeout(() => this._loadTenderExpress.declineTender(), 250);
        };

        const acceptButton = ediSuggestedPricingLayout.acceptButton;
        ediSuggestedPricingLayout.acceptButtonCallback = () => {
            ediSuggestedPricingLayout.slideOut();
            setTimeout(() => this._loadTenderExpress.acceptTenders(), 250);
        };
        if (row != null) {
            if (((row.get("initial_purpose") === "O" && row.get("requires_reply_original") === "Y") ||
                (row.get("initial_purpose") === "U" && row.get("requires_reply_change") === "Y") ||
                (row.get("initial_purpose") === "C" && row.get("requires_reply_cancel") === "Y"))
                && row.get("reply_created") !== "Y") {
                setButtonsActive(true);
            }
            else {
                setButtonsActive(false);
            }
        }
        ediSuggestedPricingLayout.initialSelectedTab = tabId;

        const slideout = new SlideoutDecorator({
            title: `Suggested Pricing - Tender ${tenderID}`,
            layout: ediSuggestedPricingLayout,
            layoutLoadListeners: async (event) => {
                await ediSuggestedPricingLayout.mainDataSource.search({ id: tenderID });
                ediSuggestedPricingLayout.mainDataSource.mode = DataSourceMode.NONE;
            },
            fillVerticalSpace: true,
            fillHorizontalSpace: true,
            transitionOptions: { direction: Alignment.CENTER },
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            addlComponents: [excludeButton, declineButton, acceptButton],
            onClose: (cancelled: boolean) => ediSuggestedPricingLayout.slideOut(),
            doAfterSlideIn: () => button.busy = false
        });
    }

    setupTextboxes(tableRow: TableRow) {
        const textboxCommodityDesc = tableRow.findComponentById("textboxCommodityDesc") as Textbox;
        if (textboxCommodityDesc) textboxCommodityDesc.captionVisible = true;

        const textboxTrailerDesc = tableRow.findComponentById("textboxTrailerDesc") as Textbox;
        if (textboxTrailerDesc) textboxTrailerDesc.captionVisible = true;

        const textboxWeight = tableRow.findComponentById("textboxWeight") as Textbox;
        if (textboxWeight) textboxWeight.captionVisible = true;

        const textboxRevenueCode = tableRow.findComponentById("textboxRevenueCode") as Textbox;
        if (textboxRevenueCode) textboxRevenueCode.captionVisible = true;

        const textboxShipmentId = tableRow.findComponentById("textboxShipmentId") as Textbox;
        if (textboxShipmentId) textboxShipmentId.captionVisible = true;

        const textboxBol = tableRow.findComponentById("textboxBol") as Textbox;
        if (textboxBol) textboxBol.captionVisible = true;

        const textboxTenderId = tableRow.findComponentById("textboxTenderId") as Textbox;
        if (textboxTenderId) textboxTenderId.captionVisible = true;

        const textboxWeightUm = tableRow.findComponentById("textboxWeightUm") as Textbox;
        if (textboxWeightUm) textboxWeightUm.captionVisible = true;
    }

    public getContextMenuItems(tableRow: TableRow, id: string): Partial<ContextMenuLabelProps>[] {
        if (this.tableLtx.selectedRow !== tableRow) {
            this.tableLtx.selectedRow = tableRow;
        }

        if(tableRow.data?.get("purpose_desc") === "Original"){
            return [
                ...ContextMenuItemCreator.fromButtons(
                    { button: this._loadTenderExpress.buttonAccept, text: "Accept Tender", imageName: ImageName.acceptTender},
                    { button: this._loadTenderExpress.buttonDecline, text: "Decline Tender", imageName: ImageName.declineTender},
                    { button: this._loadTenderExpress.buttonExclude, text: "Exclude Tender", imageName: ImageName.excludeTender },
                    { button: this.buttonSuggestedPricing, text: "Suggested Pricing"},
                    { button: this.buttonRawData, text: "Raw Data", imageName: ImageName.rawData },
                    { button: this.buttonPurposeSwap, text: "Purpose Swap", imageName: ImageName.purposeSwap, separatorAfter:true },
                ),
                ...ContextMenuItemCreator.createNavigationContextMenuItems("lme/datafusion/ViewEdiOrder", id)
            ]
        }
        else if(tableRow.data?.get("purpose_desc") === "Cancel"){
            return [
                ...ContextMenuItemCreator.fromButtons(
                    { button: this._loadTenderExpress.buttonAccept, text: "Accept Tender", imageName: ImageName.acceptTender},
                    { button: this._loadTenderExpress.buttonDecline, text: "Decline Tender", imageName: ImageName.declineTender},
                    { button: this._loadTenderExpress.buttonExclude, text: "Exclude Tender", imageName: ImageName.excludeTender },
                    { button: this.buttonSuggestedPricing, text: "Suggested Pricing"},
                    { button: this.buttonRawData, text: "Raw Data", imageName: ImageName.rawData, separatorAfter:true },
                ),
                ...ContextMenuItemCreator.createNavigationContextMenuItems("lme/datafusion/ViewEdiOrder", id)
            ]
        }
        else{
            return [
                ...ContextMenuItemCreator.fromButtons(
                    { button: this._loadTenderExpress.buttonAccept, text: "Accept Tender", imageName: ImageName.acceptTender},
                    { button: this._loadTenderExpress.buttonDecline, text: "Decline Tender", imageName: ImageName.declineTender},
                    { button: this._loadTenderExpress.buttonExclude, text: "Exclude Tender", imageName: ImageName.excludeTender },
                    { button: this.buttonSuggestedPricing, text: "Suggested Pricing"},
                    { button: this.buttonRawData, text: "Raw Data", imageName: ImageName.rawData },
                    { button: this.buttonPurposeSwap, text: "Purpose Swap", imageName: ImageName.purposeSwap },
                    { button: this.buttonLinkTender, text: "Link Change Tender", imageName: ImageName.unlinked, separatorAfter:true },

                ),
                ...ContextMenuItemCreator.createNavigationContextMenuItems("lme/datafusion/ViewEdiOrder", id)
            ]
        }
    }

/** This is an event handler for the onClick event of textboxTenderId. */
  textboxTenderIdOnClick(event: ClickEvent) {
    const tenderId = this.tableLtx.selectedRow.data.get("tender_id");
    Navigation.navigateTo("lme/datafusion/ViewEdiOrder?mode=update&key=" + tenderId, { newTab: true });
  }

  get filterPanel(): FilterPanel {
    return this._filterPanel;
  }

  openRawDataSlideout() {
        if(this.selectedRow == undefined){
            Snackbar.showWarningSnackbar("Please select a tender to view its raw data");
            return;
        }
        const tenderId = this.tableLtx.selectedRow.data?.get("tender_id");
        const templateType = this.tableLtx.selectedRow.data?.get("template_format");
        const replyCreated = this.tableLtx.selectedRow.data?.get("reply_created");
        EdiRawDataSlideout.openSlideout({title: "Tender ID " + tenderId, tenderId, templateType, replyCreated, fromViewEdiOrder: false});
    }

    async buttonPurposeSwapOnClick(event: ClickEvent) {
        if(this.selectedRow == undefined){
            Snackbar.showWarningSnackbar("Please select a tender to perform a purpose swap");
            return;
        }
            //get the purpose for the row selected
            const ediOrderRow = await new ModelEdiOrder().searchSingle({ id: this.tableLtx.selectedRow.data?.get("tender_id") });
            const purpose = ediOrderRow.get("purpose_type");
            const purposeSwap = new PurposeSwap();

            if (purpose === "O") {//change to a change and propmpt for order ID
                const dialog = new PurposeSwapDialog({ okVisible: false, title: "Order ID for Change Tender" });
                dialog.ediOrderRow = ediOrderRow;
                dialog.show().then(async response => {
                    if (response) {
                        const orderTextbox = dialog.findComponentById("textOrderId") as Textbox;
                        await purposeSwap.swapOriginalToChange(ediOrderRow, orderTextbox.text);
                    }
                });

            } else if (purpose === "U") {//it's change, change to original
                CommonDialogs.showYesNo("This will update the purpose of this tender from a change to an original.  Are you sure you want to do this?", "Change Purpose",
                    { titleProps: { imageName: ImageName.circleX, imageColor: "primary.light" }, xVisible: false }).then(clickedYes => {
                        if (clickedYes) {
                            purposeSwap.swapChangeToOriginal(ediOrderRow).then(() => {
                                this.sourceEdiOrder.search({ id: ediOrderRow.get("id") });
                            });
                        }
                    });
            }
            this.buttonPurposeSwap.tooltip = null;
        }

        async buttonLinkTenderOnClick(event: ClickEvent) {
            if(this.selectedRow == undefined){
                Snackbar.showWarningSnackbar("Please select a change tender to link to an order");
                return;
            }
            const ediOrderRow = await new ModelEdiOrder().searchSingle({ id: this.tableLtx.selectedRow.data.get("tender_id") });
            const profile = await new ModelEdiorderProfile().searchSingle({ partner_id: ediOrderRow.data["partner_id"], version: ediOrderRow.data["version"] });
            const needsReply: boolean = profile.get("rply_rquired_chang") == "Y" ? true : false;

            const dialog = new LinkTenderDialog({ okVisible: false, title: "Missing Order ID On Change Tender" });
            dialog.ediOrderRow = ediOrderRow;

            dialog.show().then(async response => {
                if (response) {
                    await this._loadTenderExpress.compareTender(ediOrderRow, profile.get("rply_rquired_chang")).catch((error) => {
                        CommonDialogs.showError(EdiUtility.getCleanErrorMessage(error));
                    });
                }
            });
        }

        parseMustRespondBy(mustRespondBy: string | null): Date | null {
            if (!mustRespondBy) {
                return null;
            }

            const parsed = new Date(mustRespondBy);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        }

        getWarningClockState(respondByDate: Date | null): WarningClockState {
            if (respondByDate == null) {
                return { visible: false };
            }

            const timeRemaining = Math.round(getTimeDiff(respondByDate) / 60000);
            if (timeRemaining > 30 && timeRemaining <= 60) {
                return {
                    visible: true,
                    imageName: ImageName.timeWarning60,
                    imageColor: "default.lighter",
                    tooltip: "Time remaining: " + timeRemaining + " minutes"
                };
            }

            if (timeRemaining > 15 && timeRemaining <= 30) {
                return {
                    visible: true,
                    imageName: ImageName.timeWarning30,
                    imageColor: "caution",
                    tooltip: "Time remaining: " + timeRemaining + " minutes"
                };
            }

            if (timeRemaining > 0 && timeRemaining <= 15) {
                return {
                    visible: true,
                    imageName: ImageName.timeWarning15,
                    imageColor: "warning",
                    tooltip: "Time remaining: " + timeRemaining + " minutes"
                };
            }

            if (timeRemaining <= 0) {
                return {
                    visible: true,
                    imageName: ImageName.alarm,
                    imageColor: "error",
                    tooltip: "Time expired"
                };
            }

            return { visible: false };
        }
}
